import React from 'react';
import clsx from 'clsx';

import { default as MUIButton, ButtonProps as MUIButtonProps } from '@material-ui/core/Button';

import './Button.css';

//
// --- Types ---

type ButtonColor = NonNullable<MUIButtonProps['color']> | 'tertiary';

export interface ButtonProps extends Omit<MUIButtonProps, 'children' | 'color'> {
  className?: string;
  children: React.ReactNode; // make required, overriding MUIButtonProps type
  /** adds support for tertiary color to list of Material UI supported colors */
  color?: ButtonColor;
  'data-testid'?: string;
}

// is MUI color type guard
function isMaterialUIColor(color: ButtonColor): color is NonNullable<MUIButtonProps['color']> {
  return color && color !== 'tertiary';
}

//
// --- Button Component ---

/**
 * Commonly styled button. Should be the base button used for all button components in this app.
 * Props passed to internal Material UI Button component.
 *
 * @see https://material-ui.com/api/button/ for available props.
 */

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    children,
    className = '',
    color = 'primary',
    'data-testid': dataTestId = 'button',
    disableElevation = true,
    variant = 'contained',
    ...remainingButtonProps
  } = props;

  return (
    <MUIButton
      className={clsx(['button', { 'button-color-tertiary': color === 'tertiary' }, className])}
      color={isMaterialUIColor(color) ? color : undefined}
      data-testid={dataTestId}
      disableElevation={disableElevation}
      ref={ref}
      variant={variant}
      {...remainingButtonProps}
    >
      {children}
    </MUIButton>
  );
});

export default Button;
